import * as React from "react";
import { Download, History, Upload } from "lucide-react";
import type { ListViewContextAction, ListViewHeaderAction } from "../../../ui/components/ListView";
import type { DataTransferEntity } from "../../lib/api/data-transfer.types";
import { ExportModal, type ExportPreviewItem } from "./ExportModal";
import { ImportModal } from "./ImportModal";
import { CheckpointsModal } from "./CheckpointsModal";

export type { ExportPreviewItem };

export interface UseListImportExportOptions {
  entity: DataTransferEntity;
  entityLabel: string;
  enableImport?: boolean;
  enableExport?: boolean;
  /** Defaults to true when import is enabled (checkpoints are created on apply). */
  enableCheckpoints?: boolean;
  totalCount: number;
  /** Live selection from the parent ListView */
  selectedIds: string[];
  /** Rows for the export preview table */
  previewItems?: ExportPreviewItem[];
  search?: string;
  defaultFormat?: "csv" | "json" | "zip";
  onImported?: () => void;
}

/**
 * Builds header + context actions and modal elements for ListView import/export.
 */
export function useListImportExport(opts: UseListImportExportOptions) {
  const {
    entity,
    entityLabel,
    enableImport = false,
    enableExport = false,
    enableCheckpoints: enableCheckpointsOpt,
    totalCount,
    selectedIds,
    previewItems = [],
    search,
    defaultFormat,
    onImported,
  } = opts;

  const enableCheckpoints = enableCheckpointsOpt ?? enableImport;

  const [exportOpen, setExportOpen] = React.useState(false);
  const [importOpen, setImportOpen] = React.useState(false);
  const [checkpointsOpen, setCheckpointsOpen] = React.useState(false);
  const [exportIds, setExportIds] = React.useState<string[]>([]);

  const headerActions = React.useMemo((): ListViewHeaderAction[] => {
    const actions: ListViewHeaderAction[] = [];
    if (enableExport) {
      actions.push({
        id: "export",
        label: "Export",
        icon: <Upload size={14} />,
        variant: "outline",
        onClick: () => {
          setExportIds(selectedIds);
          setExportOpen(true);
        },
      });
    }
    if (enableImport) {
      actions.push({
        id: "import",
        label: "Import",
        icon: <Download size={14} />,
        variant: "outline",
        onClick: () => setImportOpen(true),
      });
    }
    if (enableCheckpoints) {
      actions.push({
        id: "checkpoints",
        label: "Checkpoints",
        icon: <History size={14} />,
        variant: "outline",
        onClick: () => setCheckpointsOpen(true),
      });
    }
    return actions;
  }, [enableExport, enableImport, enableCheckpoints, selectedIds]);

  const exportContextAction = React.useMemo((): ListViewContextAction | null => {
    if (!enableExport) return null;
    return {
      id: "export",
      label: "Export",
      icon: <Upload size={14} />,
      bulk: true,
      onClick: (ids) => {
        setExportIds(ids);
        setExportOpen(true);
      },
    };
  }, [enableExport]);

  const modals = (
    <>
      {enableExport && (
        <ExportModal
          open={exportOpen}
          onClose={() => setExportOpen(false)}
          entity={entity}
          entityLabel={entityLabel}
          selectedIds={exportIds}
          totalCount={totalCount}
          previewItems={previewItems}
          search={search}
          defaultFormat={defaultFormat}
        />
      )}
      {enableImport && (
        <ImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          expectedEntity={entity}
          entityLabel={entityLabel}
          onApplied={onImported}
        />
      )}
      {enableCheckpoints && (
        <CheckpointsModal
          open={checkpointsOpen}
          onClose={() => setCheckpointsOpen(false)}
          entity={entity}
          entityLabel={entityLabel}
          onRestored={onImported}
        />
      )}
    </>
  );

  return { headerActions, exportContextAction, modals };
}
